import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/lib/i18n";
import { PREF_KEYS, readPref, writePref } from "@/lib/storage";
import { areAmountsHidden, setAmountsHidden } from "@/lib/format";
import { debugWarn } from "@/lib/debug";

// Root font-size scale (Tailwind uses rem, so the whole UI scales proportionally).
export const FONT_SIZES = {
  sm: { px: 16, labelKey: "pref_font_sm" },
  md: { px: 18, labelKey: "pref_font_md" },
  lg: { px: 20, labelKey: "pref_font_lg" },
  xl: { px: 22, labelKey: "pref_font_xl" },
};
export const FONT_SIZE_ORDER = ["sm", "md", "lg", "xl"];
export const DEFAULT_FONT_SIZE = "md";
const FONT_KEY = PREF_KEYS.fontSize;

export const THEMES = ["system", "light", "dark"];
export const DEFAULT_THEME = "system";
const THEME_KEY = PREF_KEYS.theme;
const mq = () => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia("(prefers-color-scheme: dark)") : null);

function readStoredFont() {
  const v = readPref(FONT_KEY);
  return v && FONT_SIZES[v] ? v : DEFAULT_FONT_SIZE;
}
function readStoredTheme() {
  const v = readPref(THEME_KEY);
  return v && THEMES.includes(v) ? v : DEFAULT_THEME;
}

export function applyFontSize(size) {
  const s = FONT_SIZES[size] ? size : DEFAULT_FONT_SIZE;
  document.documentElement.style.fontSize = `${FONT_SIZES[s].px}px`;
  document.documentElement.setAttribute("data-font-size", s);
}

export function resolveTheme(theme) {
  if (theme === "light" || theme === "dark") return theme;
  const m = mq();
  return m && m.matches ? "dark" : "light";
}

export function applyTheme(theme) {
  const t = THEMES.includes(theme) ? theme : DEFAULT_THEME;
  const resolved = resolveTheme(t);
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
  root.setAttribute("data-theme", t);
  root.setAttribute("data-resolved-theme", resolved);
  root.style.colorScheme = resolved;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", resolved === "dark" ? "#17120e" : "#3d4a2c"); // warm charcoal / deep olive (Phase 49 theme)
  return resolved;
}

// Apply stored values ASAP (before React renders) to avoid a flash of the wrong theme/size.
try {
  applyFontSize(readStoredFont());
  applyTheme(readStoredTheme());
} catch (e) {
  // SSR / test environments have no document — React applies the preferences on mount instead
  debugWarn("initial theme/font apply skipped", e);
}

const PreferencesContext = createContext({
  fontSize: DEFAULT_FONT_SIZE, setFontSize: () => {}, theme: DEFAULT_THEME, resolvedTheme: "light", setTheme: () => {}, saving: false,
  hideAmounts: false, setHideAmounts: () => {},
});

/** Font-size preference: local state, applied to the DOM and persisted in local storage. */
function useFontSizePreference() {
  const [fontSize, setFontSizeState] = useState(readStoredFont);
  useEffect(() => {
    applyFontSize(fontSize);
    writePref(FONT_KEY, fontSize);
  }, [fontSize]);
  return [fontSize, setFontSizeState];
}

/** Theme preference: applied to the DOM, persisted locally, and re-resolved when the OS scheme changes while on "system". */
function useThemePreference() {
  const [theme, setThemeState] = useState(readStoredTheme);
  const [resolvedTheme, setResolvedTheme] = useState(() => resolveTheme(readStoredTheme()));
  useEffect(() => {
    setResolvedTheme(applyTheme(theme));
    writePref(THEME_KEY, theme);
    if (theme !== "system") return undefined;
    const m = mq();
    if (!m) return undefined;
    const onChange = () => setResolvedTheme(applyTheme("system"));
    if (m.addEventListener) m.addEventListener("change", onChange); else m.addListener(onChange);
    return () => { if (m.removeEventListener) m.removeEventListener("change", onChange); else m.removeListener(onChange); };
  }, [theme]);
  return [theme, setThemeState, resolvedTheme];
}

/**
 * Server-side value wins when the account has one; otherwise the local choice is queued (into `patch[key]`) to be
 * pushed to the account. `{ server, local, isValid, setLocal }` describe one preference.
 */
function adoptOrQueue(patch, key, { server, local, isValid, setLocal }) {
  if (server && isValid(server)) {
    if (server !== local) setLocal(server);
  } else {
    patch[key] = local;
  }
}

const isFontSize = (v) => Boolean(FONT_SIZES[v]);
const isTheme = (v) => THEMES.includes(v);

/**
 * On sign-in (user id changes): adopt the account's display preferences; push local ones if the account has none.
 * Returns a ref holding the id of the account whose preferences are currently in sync (null when signed out).
 */
function useAccountPreferenceSync(user, { fontSize, setFontSize, theme, setTheme }) {
  const syncedUserId = useRef(null);
  // Latest local choices, readable from the sign-in effect without re-running it on every change.
  const localPrefs = useRef({ fontSize, theme });
  localPrefs.current = { fontSize, theme };
  useEffect(() => {
    if (!user) { syncedUserId.current = null; return; }
    if (syncedUserId.current === user.id) return;
    syncedUserId.current = user.id;
    const prefs = user.preferences || {};
    const local = localPrefs.current;
    const patch = {};
    adoptOrQueue(patch, "font_size", { server: prefs.font_size, local: local.fontSize, isValid: isFontSize, setLocal: setFontSize });
    adoptOrQueue(patch, "theme", { server: prefs.theme, local: local.theme, isValid: isTheme, setLocal: setTheme });
    if (Object.keys(patch).length) api.put("/auth/me", { preferences: patch }).catch((e) => debugWarn("could not push local preferences to the account", e));
  }, [user, setFontSize, setTheme]);
  return syncedUserId;
}

/** Persist the UI language to the account when it changes while signed in (no-op once the account already matches). */
function useLanguageSync(user, setUser, lang, syncedUserId) {
  useEffect(() => {
    if (!user || syncedUserId.current !== user.id) return;
    if (user.preferred_language !== lang) {
      api.put("/auth/me", { preferred_language: lang }).then(({ data }) => setUser(data)).catch((e) => debugWarn("could not save preferred language", e));
    }
  }, [lang, user, setUser, syncedUserId]);
}

/** Save a display-preference patch to the account (local value is kept even if the server save fails). */
function usePersistPreference(user, setUser) {
  const [saving, setSaving] = useState(false);
  const persist = useCallback(async (patch) => {
    if (!user) return;
    setSaving(true);
    try {
      const { data } = await api.put("/auth/me", { preferences: patch });
      setUser(data);
    } catch (e) {
      // keep the local preference even if the server save fails (it is re-pushed on the next sign-in)
      debugWarn("preference save failed; kept locally", e);
    } finally {
      setSaving(false);
    }
  }, [user, setUser]);
  return [persist, saving];
}

export function PreferencesProvider({ children }) {
  const { user, setUser } = useAuth();
  const { lang, setLang } = useLang();
  const [fontSize, setFontSizeState] = useFontSizePreference();
  const [theme, setThemeState, resolvedTheme] = useThemePreference();
  const [hideAmounts, setHideAmountsState] = useState(areAmountsHidden);
  const syncedUserId = useAccountPreferenceSync(user, { fontSize, setFontSize: setFontSizeState, theme, setTheme: setThemeState });
  useLanguageSync(user, setUser, lang, syncedUserId);
  const [persist, saving] = usePersistPreference(user, setUser);

  const setFontSize = useCallback(async (size) => {
    if (!FONT_SIZES[size]) return;
    setFontSizeState(size);
    await persist({ font_size: size });
  }, [persist, setFontSizeState]);

  const setTheme = useCallback(async (next) => {
    if (!THEMES.includes(next)) return;
    setThemeState(next);
    await persist({ theme: next });
  }, [persist, setThemeState]);

  // Device-local (privacy): the flag is flipped before the state so the re-mounted page formats with the new value.
  const setHideAmounts = useCallback((next) => {
    setAmountsHidden(next);
    setHideAmountsState(Boolean(next));
  }, []);

  // Split into a state slice and a stable actions slice so consumers only re-render when a displayed value changes.
  const state = useMemo(() => ({ fontSize, theme, resolvedTheme, saving, hideAmounts }), [fontSize, theme, resolvedTheme, saving, hideAmounts]);
  const actions = useMemo(() => ({ setFontSize, setTheme, setLang, setHideAmounts }), [setFontSize, setTheme, setLang, setHideAmounts]);
  const value = useMemo(() => ({ ...state, ...actions }), [state, actions]);
  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export const usePreferences = () => useContext(PreferencesContext);
